import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { updateRecordActivity } from "../../../features/sales/SalesActions";

// App-level sales recording player. The pop-out dialog hands its audio here when
// it closes, so playback continues as a notification banner (UX contract: Sales
// Recordings player, intent-ui-patterns.md, Levi 2026-10-22).
//
// Two contexts on purpose: the list renders ~470 dialogs, and they only need the
// stable actions. Playback state changes several times a second and only the
// banner reads it.

const RecordingPlayerActionsContext = createContext(null);
const RecordingPlayerStateContext = createContext(null);

export const useRecordingPlayerActions = () => useContext(RecordingPlayerActionsContext);
export const useRecordingPlayerState = () => useContext(RecordingPlayerStateContext);

export const OPEN_RECORDING_EVENT = "kai-open-sales-recording";

const positionKey = (id) => `kai.salesRecording.position.${id}`;

export const savedPosition = (id) => {
  const value = Number(localStorage.getItem(positionKey(id)));
  return Number.isFinite(value) && value > 0 ? value : 0;
};

export const storePosition = (id, secs) => {
  if (!id) return;
  if (!Number.isFinite(secs) || secs < 5) {
    localStorage.removeItem(positionKey(id));
    return;
  }
  localStorage.setItem(positionKey(id), String(Math.floor(secs)));
};

export const recordingTitle = (fields) =>
  [fields?.["Sales Rep"], fields?.["Recording Date"]].filter(Boolean).join(" · ") || "Sales recording";

export const recordingTrack = (item) => {
  const attachment = item?.fields?.["Portal Recording - Attachment"]?.[0];
  if (!attachment?.url) return null;
  return { id: item.id, title: recordingTitle(item.fields), url: attachment.url, fields: item.fields };
};

export const RecordingPlayerProvider = ({ children }) => {
  const dispatch = useDispatch();
  const audioRef = useRef(null);
  const trackRef = useRef(null);
  const queueRef = useRef([]);
  const countRef = useRef(null);
  const playStartedAtRef = useRef(null);
  const pendingSeekRef = useRef(0);
  const pendingResumeRef = useRef(null);
  const pendingOpenRef = useRef(null);
  const lastSavedRef = useRef(0);

  const [track, setTrack] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const reportSeconds = useCallback(
    (t) => {
      if (!playStartedAtRef.current) return;
      const elapsed = Math.floor((Date.now() - playStartedAtRef.current) / 1000);
      playStartedAtRef.current = null;
      if (t && elapsed > 0) dispatch(updateRecordActivity({ recordingId: t.id, watchedSecs: elapsed }));
    },
    [dispatch]
  );

  const loadTrack = useCallback((t, startAt, autoplay) => {
    const el = audioRef.current;
    trackRef.current = t;
    pendingSeekRef.current = startAt || 0;
    lastSavedRef.current = startAt || 0;
    setTrack(t);
    setTime(startAt || 0);
    setDuration(0);
    el.src = t.url;
    el.load();
    if (autoplay) el.play().catch(() => {});
  }, []);

  // Next / Previous / auto-advance: play a queue item and count the play, like the pop-out's Play does.
  const playQueueItem = useCallback(
    (t) => {
      reportSeconds(trackRef.current);
      if (countRef.current) countRef.current(t.id);
      loadTrack(t, savedPosition(t.id), true);
    },
    [loadTrack, reportSeconds]
  );

  const stepQueue = useCallback(
    (direction) => {
      const queue = queueRef.current;
      if (!queue.length) return;
      const index = queue.findIndex((item) => item.id === trackRef.current?.id);
      const target = index === -1 ? queue[0] : queue[(index + direction + queue.length) % queue.length];
      playQueueItem(target);
    },
    [playQueueItem]
  );

  // Called by the pop-out dialog just before it closes while its audio is playing.
  const handoffFromDialog = useCallback(
    (t, secs) => {
      loadTrack(t, secs, true);
    },
    [loadTrack]
  );

  const togglePlay = useCallback(() => {
    const el = audioRef.current;
    if (!el || !trackRef.current) return;
    if (el.paused) el.play().catch(() => {});
    else el.pause();
  }, []);

  const seekTo = useCallback((secs) => {
    const el = audioRef.current;
    if (el) el.currentTime = secs;
    setTime(secs);
  }, []);

  const skipBy = useCallback((delta) => {
    const el = audioRef.current;
    if (!el) return;
    const max = Number.isFinite(el.duration) ? el.duration : el.currentTime + delta;
    el.currentTime = Math.min(Math.max(el.currentTime + delta, 0), max);
  }, []);

  const closeTrack = useCallback(() => {
    const el = audioRef.current;
    const t = trackRef.current;
    if (el) {
      el.pause();
      if (t) storePosition(t.id, el.currentTime);
      reportSeconds(t);
      el.removeAttribute("src");
      el.load();
    }
    trackRef.current = null;
    setTrack(null);
    setPlaying(false);
  }, [reportSeconds]);

  // Open: hand the recording back to the Sales Recordings page, where its dialog resumes at the same instant.
  const requestOpen = useCallback(() => {
    const el = audioRef.current;
    const t = trackRef.current;
    if (!t || !el) return;
    const secs = el.currentTime;
    const wasPlaying = !el.paused;
    el.pause();
    storePosition(t.id, secs);
    reportSeconds(t);
    pendingResumeRef.current = { id: t.id, time: secs, playing: wasPlaying };
    pendingOpenRef.current = { id: t.id };
    trackRef.current = null;
    setTrack(null);
    setPlaying(false);
    window.dispatchEvent(new CustomEvent(OPEN_RECORDING_EVENT, { detail: { id: t.id } }));
  }, [reportSeconds]);

  // Each dialog calls this with its own id, on mount and on the live event. Only one can claim it.
  const claimOpenRequest = useCallback((id) => {
    if (!pendingOpenRef.current || pendingOpenRef.current.id !== id) return false;
    pendingOpenRef.current = null;
    return true;
  }, []);

  const consumeResume = useCallback((id) => {
    const pending = pendingResumeRef.current;
    if (!pending || pending.id !== id) return null;
    pendingResumeRef.current = null;
    return pending;
  }, []);

  const setQueue = useCallback((items) => {
    queueRef.current = items;
  }, []);

  const setCountHandler = useCallback((fn) => {
    countRef.current = fn;
  }, []);

  const onAudioPlay = () => {
    setPlaying(true);
    if (!playStartedAtRef.current) playStartedAtRef.current = Date.now();
  };

  const onAudioPause = () => {
    setPlaying(false);
    const el = audioRef.current;
    if (trackRef.current && el) storePosition(trackRef.current.id, el.currentTime);
    reportSeconds(trackRef.current);
  };

  const onAudioEnded = () => {
    setPlaying(false);
    if (trackRef.current) storePosition(trackRef.current.id, 0);
    reportSeconds(trackRef.current);
    const queue = queueRef.current;
    const index = queue.findIndex((item) => item.id === trackRef.current?.id);
    const next = index === -1 ? queue[0] : queue[index + 1];
    if (next) playQueueItem(next);
  };

  const onAudioTime = () => {
    const el = audioRef.current;
    if (!el) return;
    setTime(el.currentTime);
    if (trackRef.current && Math.abs(el.currentTime - lastSavedRef.current) >= 5) {
      lastSavedRef.current = el.currentTime;
      storePosition(trackRef.current.id, el.currentTime);
    }
  };

  const onAudioMetadata = () => {
    const el = audioRef.current;
    if (!el) return;
    setDuration(Number.isFinite(el.duration) ? el.duration : 0);
    if (pendingSeekRef.current > 0) {
      el.currentTime = pendingSeekRef.current;
      pendingSeekRef.current = 0;
    }
  };

  const actions = useMemo(
    () => ({
      handoffFromDialog,
      togglePlay,
      seekTo,
      skipBy,
      next: () => stepQueue(1),
      previous: () => stepQueue(-1),
      closeTrack,
      requestOpen,
      claimOpenRequest,
      consumeResume,
      setQueue,
      setCountHandler,
    }),
    [
      handoffFromDialog,
      togglePlay,
      seekTo,
      skipBy,
      stepQueue,
      closeTrack,
      requestOpen,
      claimOpenRequest,
      consumeResume,
      setQueue,
      setCountHandler,
    ]
  );

  const state = useMemo(
    () => ({ track, playing, time, duration }),
    [track, playing, time, duration]
  );

  return (
    <RecordingPlayerActionsContext.Provider value={actions}>
      <RecordingPlayerStateContext.Provider value={state}>
        {children}
        <audio
          ref={audioRef}
          preload="auto"
          className="hidden"
          onPlay={onAudioPlay}
          onPause={onAudioPause}
          onEnded={onAudioEnded}
          onTimeUpdate={onAudioTime}
          onLoadedMetadata={onAudioMetadata}
          onDurationChange={onAudioMetadata}
        />
      </RecordingPlayerStateContext.Provider>
    </RecordingPlayerActionsContext.Provider>
  );
};

export default RecordingPlayerProvider;
